/**
 * NotificacionesPage — vive bajo PortalLayout.
 */

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { PageHeader } from '@app/components/common/PageHeader';
import { EmptyState } from '@app/components/common/EmptyState';
import {
  useNotificationsList,
  useMarkAsRead,
  useMarkAllAsRead,
} from '@modules/notifications/presentation/hooks/useNotifications';
import { getErrorMessage } from '@shared/infrastructure/http/api.error';
import { formatDate } from '@shared/utils/formatters';
import { toast } from 'sonner';
import { Bell, Check, CheckCheck } from 'lucide-react';
import { cn } from '@shared/utils/cn';

export function NotificacionesPage() {
  const { data: notifications = [], isLoading } = useNotificationsList();
  const markAsRead = useMarkAsRead();
  const markAllAsRead = useMarkAllAsRead();

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  const handleMarkOne = async (id: string) => {
    try {
      await markAsRead.mutateAsync(id);
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const handleMarkAll = async () => {
    try {
      await markAllAsRead.mutateAsync();
      toast.success('Todas marcadas como leídas');
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Notificaciones"
        subtitle={
          unreadCount > 0
            ? `${unreadCount} sin leer`
            : 'Estás al día con tus notificaciones'
        }
        action={
          unreadCount > 0 ? (
            <Button
              variant="outline"
              size="sm"
              className="rounded-full"
              onClick={handleMarkAll}
              disabled={markAllAsRead.isPending}
            >
              <CheckCheck className="mr-1 h-4 w-4" aria-hidden="true" />
              Marcar todas
            </Button>
          ) : undefined
        }
      />

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-24 w-full rounded-2xl" />
          ))}
        </div>
      ) : notifications.length === 0 ? (
        <EmptyState
          icon={Bell}
          title="Sin notificaciones"
          description="Cuando tengamos algo importante para contarte, aparecerá acá."
        />
      ) : (
        <ul className="space-y-2">
          {notifications.map((n) => (
            <li
              key={n.id}
              className={cn(
                'rounded-2xl border p-4 shadow-xs transition-colors',
                n.is_read
                  ? 'border-border bg-card'
                  : 'border-sidebar-primary/30 bg-sidebar-accent/40',
              )}
            >
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-foreground">{n.title}</p>
                  <p className="mt-0.5 text-sm text-muted-foreground">{n.message}</p>
                  <p className="mt-2 text-xs text-muted-foreground">{formatDate(n.created_at)}</p>
                </div>
                {!n.is_read && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 shrink-0 rounded-full"
                    onClick={() => handleMarkOne(n.id)}
                    disabled={markAsRead.isPending}
                  >
                    <Check className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
                    Marcar leída
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
